import * as React from "react";
import { CheckCircle2, CircleAlert, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import {
  closeToast,
  TOAST_DURATION_MS,
  toastRemainingMs,
  type ToastButton,
  type ToastCloseReason,
  type ToastItem,
  type ToastTone,
} from "@/lib/toastStore";

/** Closes the toast an action sits in: an action click closes it (as Radix and sonner did). */
const ToastItemContext = React.createContext<((reason: ToastCloseReason) => void) | null>(null);

type ToastActionProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  /** Kept for the callers written for Radix; the button's own text is its accessible name. */
  altText?: string;
};

/** A button on a toast ("Print again", "Open table"). It closes the toast unless its onClick calls preventDefault(). */
const ToastAction = React.forwardRef<HTMLButtonElement, ToastActionProps>(
  ({ className, altText: _altText, onClick, type = "button", ...props }, ref) => {
    const close = React.useContext(ToastItemContext);
    return (
      <button
        ref={ref}
        type={type}
        className={cn(
          "touch-hit press inline-flex h-8 shrink-0 items-center justify-center rounded-md border border-line-control bg-background px-3 text-sm font-semibold text-ink hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
          "group-data-[tone=destructive]:border-white/45 group-data-[tone=destructive]:bg-transparent group-data-[tone=destructive]:text-destructive-foreground group-data-[tone=destructive]:hover:bg-white/15 group-data-[tone=destructive]:focus-visible:ring-white group-data-[tone=destructive]:focus-visible:ring-offset-destructive",
          className,
        )}
        onClick={(event) => {
          onClick?.(event);
          if (!event.defaultPrevented) close?.("action");
        }}
        {...props}
      />
    );
  },
);
ToastAction.displayName = "ToastAction";

type ToastActionElement = React.ReactElement<typeof ToastAction>;

/** Re-renders a few times a second while `on`, for a countdown shown as a number. */
function useTicker(on: boolean) {
  const [, tick] = React.useReducer((n: number) => n + 1, 0);
  React.useEffect(() => {
    if (!on) return;
    const timer = setInterval(tick, 250);
    return () => clearInterval(timer);
  }, [on]);
}

/**
 * The 20px ring that drains over the toast's 5 seconds, beside its X. It holds
 * while the countdown is paused. With reduced motion it stays still and shows
 * the seconds left instead.
 */
function CountdownRing({ toast }: { toast: ToastItem }) {
  const reduced = usePrefersReducedMotion();
  const running = toast.open && toast.since !== null;
  // The countdown may have run before the ring mounted (folded into "+N more"): start where it is.
  const [elapsed] = React.useState(() => TOAST_DURATION_MS - toastRemainingMs(toast));
  useTicker(reduced && running);
  const seconds = Math.max(0, Math.ceil(toastRemainingMs(toast) / 1000));
  return (
    <span
      aria-hidden="true"
      data-testid="toast-countdown"
      data-paused={running ? undefined : ""}
      className="relative inline-flex h-5 w-5 shrink-0 items-center justify-center text-ink-2 group-data-[tone=destructive]:text-destructive-foreground"
    >
      <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5 -rotate-90">
        <circle cx="10" cy="10" r="8" strokeWidth="2" className="stroke-current opacity-20" />
        {!reduced && (
          <circle
            data-testid="toast-countdown-arc"
            cx="10"
            cy="10"
            r="8"
            strokeWidth="2"
            pathLength={100}
            strokeDasharray="100"
            className="animate-toast-ring stroke-current"
            style={{
              animationDuration: `${TOAST_DURATION_MS}ms`,
              animationDelay: `-${elapsed}ms`,
              animationPlayState: running ? "running" : "paused",
            }}
          />
        )}
      </svg>
      {reduced && (
        <span data-testid="toast-countdown-seconds" className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold leading-none tabular">
          {seconds}
        </span>
      )}
    </span>
  );
}

const TONE_CLASS: Record<ToastTone, string> = {
  default: "border-line bg-background text-foreground",
  success: "border-line bg-background text-foreground",
  error: "border-line bg-background text-foreground",
  destructive: "border-destructive bg-destructive text-destructive-foreground",
};

function ToneIcon({ tone }: { tone: ToastTone }) {
  if (tone === "success") {
    return <CheckCircle2 aria-hidden="true" className="mt-2 h-4 w-4 shrink-0 text-[hsl(var(--status-served-dot))]" />;
  }
  if (tone === "error") return <CircleAlert aria-hidden="true" className="mt-2 h-4 w-4 shrink-0 text-destructive" />;
  return null;
}

function isToastButton(action: ToastItem["action"]): action is ToastButton {
  return !!action && !React.isValidElement(action);
}

/**
 * One toast: title, description, its action, then the countdown ring and the X.
 * A sticky toast (stays until closed) has the X and no ring. Destructive and
 * error toasts are announced assertively, the rest politely.
 */
function ToastCard({ toast }: { toast: ToastItem }) {
  const { id } = toast;
  const close = React.useCallback((reason: ToastCloseReason) => closeToast(id, reason), [id]);
  const assertive = toast.tone === "destructive" || toast.tone === "error";
  const action = isToastButton(toast.action) ? (
    <ToastAction onClick={toast.action.onClick}>{toast.action.label}</ToastAction>
  ) : (
    toast.action
  );

  return (
    <ToastItemContext.Provider value={close}>
      <li
        data-toast=""
        data-toast-id={id}
        data-state={toast.open ? "open" : "closed"}
        data-tone={toast.tone}
        data-sticky={toast.sticky ? "" : undefined}
        role={assertive ? "alert" : "status"}
        aria-live={assertive ? "assertive" : "polite"}
        aria-atomic="true"
        aria-hidden={toast.open ? undefined : true}
        onKeyDown={(event) => {
          if (event.key !== "Escape") return;
          event.stopPropagation();
          close("user");
        }}
        className={cn(
          "group pointer-events-auto relative flex w-full items-start gap-3 rounded-lg border py-2 pl-4 pr-2 elev-1",
          TONE_CLASS[toast.tone],
          toast.open
            ? "animate-toast-in motion-reduce:animate-none"
            : "pointer-events-none animate-toast-out motion-reduce:invisible motion-reduce:animate-none",
        )}
      >
        <ToneIcon tone={toast.tone} />
        <div className="min-w-0 flex-1 py-1.5">
          {toast.title && <div className="break-words text-sm font-semibold leading-5">{toast.title}</div>}
          {toast.description && (
            <div
              className={cn(
                "break-words text-sm leading-5",
                toast.tone === "destructive" ? "opacity-90" : "text-ink-2",
                toast.title && "mt-0.5",
              )}
            >
              {toast.description}
            </div>
          )}
          {action && <div className="mt-2.5 flex flex-wrap gap-2">{action}</div>}
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {!toast.sticky && <CountdownRing key={toast.version} toast={toast} />}
          <button
            type="button"
            aria-label="Dismiss notification"
            onClick={() => close("user")}
            className="touch-hit inline-flex h-8 w-8 items-center justify-center rounded-md text-ink-2 hover:bg-muted hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-data-[tone=destructive]:text-destructive-foreground/85 group-data-[tone=destructive]:hover:bg-white/15 group-data-[tone=destructive]:hover:text-destructive-foreground group-data-[tone=destructive]:focus-visible:ring-white"
          >
            <X aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>
      </li>
    </ToastItemContext.Provider>
  );
}

export { type ToastActionElement, ToastAction, ToastCard };
